import { useEffect, useRef, useState } from "react"
import { Monitor01, Phone01 } from "@untitledui/icons"
import type { IntakeConfig, ShopBranding } from "@/lib/intake/intake-config"
import { cx } from "@/lib/utils/cx"

// Live preview of the consumer flow, framed as the phone it will almost
// always run on. Renders /embed/:shopId?preview=1 (same-origin iframe) and
// pushes the UNSAVED form state into it via postMessage so edits show up
// before the shop commits — the embed page in preview mode never creates a
// lead. Until the /embed route ships (later phase) the iframe shows the app
// 404; the frame itself is the contract.
//
// Message contract (parent → child, same origin both ways):
//   { type: "etx-intake:preview-config", shopId, config: { intake, branding } }

interface IntakePreviewProps {
  shopId: string
  intake: IntakeConfig
  branding: ShopBranding
}

const PUSH_DEBOUNCE_MS = 150

export function IntakePreview({ shopId, intake, branding }: IntakePreviewProps) {
  const [device, setDevice] = useState<"phone" | "desktop">("phone")
  const frameRef = useRef<HTMLIFrameElement>(null)
  // Latest config, readable from the load handler without re-mounting the
  // iframe on every keystroke.
  const configRef = useRef({ intake, branding })
  configRef.current = { intake, branding }

  const push = () => {
    frameRef.current?.contentWindow?.postMessage(
      {
        type: "etx-intake:preview-config",
        shopId,
        config: configRef.current,
      },
      window.location.origin,
    )
  }

  // Debounced push on every edit — typing in "Welcome title" repaints the
  // phone 150ms after the last keystroke.
  useEffect(() => {
    const t = window.setTimeout(push, PUSH_DEBOUNCE_MS)
    return () => window.clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intake, branding, shopId])

  const src = `/embed/${shopId}?preview=1`

  return (
    <div className="flex flex-col items-center gap-3">
      <div className="flex items-center gap-1 rounded-lg bg-zinc-100 p-1">
        <DeviceTab
          active={device === "phone"}
          icon={Phone01}
          label="Phone"
          onClick={() => setDevice("phone")}
        />
        <DeviceTab
          active={device === "desktop"}
          icon={Monitor01}
          label="Desktop"
          onClick={() => setDevice("desktop")}
        />
      </div>

      {device === "phone" ? (
        // Phone bezel — a REAL 390×760 viewport (the flow's phone breakpoint)
        // scaled down visually so media queries inside see a true phone.
        <div className="rounded-[30px] border-[5px] border-zinc-900 bg-zinc-900 shadow-xl shadow-zinc-900/20">
          <div className="relative h-[608px] w-[312px] overflow-hidden rounded-[25px] bg-white">
            <div
              aria-hidden
              className="absolute left-1/2 top-2 z-10 h-3.5 w-20 -translate-x-1/2 rounded-full bg-zinc-900"
            />
            <iframe
              ref={frameRef}
              src={src}
              title="Consumer intake preview"
              onLoad={push}
              className="block h-[760px] w-[390px] origin-top-left scale-[0.8]"
            />
          </div>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-lg shadow-zinc-900/[0.06]">
          <div className="flex items-center gap-1.5 border-b border-zinc-100 bg-zinc-50 px-3 py-2">
            <span className="size-2 rounded-full bg-zinc-300" aria-hidden />
            <span className="size-2 rounded-full bg-zinc-300" aria-hidden />
            <span className="size-2 rounded-full bg-zinc-300" aria-hidden />
          </div>
          {/* True desktop viewport, scaled to fit the rail. */}
          <div className="h-[302px] w-[430px] overflow-hidden">
            <iframe
              ref={frameRef}
              src={src}
              title="Consumer intake preview"
              onLoad={push}
              className="block h-[720px] w-[1024px] origin-top-left scale-[0.42]"
            />
          </div>
        </div>
      )}

      <p className="text-center text-[11.5px] leading-snug text-zinc-400">
        Live preview — shows unsaved changes.
      </p>
    </div>
  )
}

function DeviceTab({
  active,
  icon: Icon,
  label,
  onClick,
}: {
  active: boolean
  icon: React.ComponentType<{ className?: string }>
  label: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cx(
        "flex h-8 items-center gap-1.5 rounded-md px-3 text-[12px] font-semibold transition-colors",
        active ? "bg-white text-zinc-900 shadow-sm" : "text-zinc-500 hover:text-zinc-800",
      )}
    >
      <Icon className="size-3.5" aria-hidden />
      {label}
    </button>
  )
}
